import Component from '@glimmer/component'
import { service } from '@ember/service'
import { reportingLegend as legend } from 'teacher-ui-client/utils/giraffe/highcharts-config'
import { colors } from '@colors'
import convertDateFormat from 'teacher-ui-client/utils/giraffe/convert-date-format'
import { formatNumber } from 'teacher-ui-client/utils/giraffe/formatter'
import type { IntlService } from 'ember-intl'
import type { LegendOptions } from 'highcharts'
import { SummaryBox, type Stat } from 'teacher-ui-client/components/giraffe/summary-box'
import { SeriesColumn, type SeriesColumnOptions } from 'teacher-ui-client/components/giraffe/primitives/series-column'
import LeftSplitChart from 'teacher-ui-client/components/giraffe/left-split-chart'
import { SummaryBoxColorScheme } from 'teacher-ui-client/components/giraffe/summary-box/themes'
import type { AwardsEarnedTotalsByGradeData } from 'teacher-ui-client/utils/student-events/fetchers/awards-earned-totals'
import type { AwardsEarnedByMonthEventsData } from 'teacher-ui-client/utils/student-events/fetchers/awards-earned-by-month-events'

interface Args {
  data: AwardsEarnedTotalsByGradeData | AwardsEarnedByMonthEventsData
  grade?: string
  product: 're' | 'maths'
  summaryTitle: string
  title: string
  yAxisTitle: string
}

interface Signature {
  Element: HTMLDivElement
  Args: Args
}

export class AwardsEarnedCertificatesTotals extends Component<Signature> {
  @service intl!: IntlService

  /**
   * Chart legend for SeriesColumn
   */
  readonly legend: LegendOptions = legend('left', 'top', 30, -15)

  /**
   * List of category data, extracted from the input data.
   * Formats month data for SeriesColumn labels.
   * Converts month dates into short names.
   */
  get categories(): string[] {
    return this.args.data.months?.map((monthData) => convertDateFormat(monthData.month)) ?? []
  }

  /**
   * List of chart data, extracted from the input data.
   */
  get chartData(): SeriesColumnOptions[] {
    const { intl } = this
    const { product } = this.args

    const goldLabel = intl.t(`reporting.charts.awardsEarned.certificates.byMonth.goldLabel.${product}`)
    const silverLabel = intl.t(`reporting.charts.awardsEarned.certificates.byMonth.silverLabel.${product}`)
    const bronzeLabel = intl.t(`reporting.charts.awardsEarned.certificates.byMonth.bronzeLabel.${product}`)

    const monthData = this.args.data.months ?? []
    const bronzeData = monthData.map((item) => item.bronze)
    const silverData = monthData.map((item) => item.silver)
    const goldData = monthData.map((item) => item.gold)

    return [
      {
        name: goldLabel,
        data: goldData,
        color: colors.medalGold,
        legendIndex: 3,
        type: 'column',
      },
      {
        name: silverLabel,
        data: silverData,
        color: colors.medalSilver,
        legendIndex: 2,
        type: 'column',
      },
      {
        name: bronzeLabel,
        data: bronzeData,
        color: colors.medalBronze,
        legendIndex: 1,
        type: 'column',
      },
    ]
  }
  /**
   * Color scheme for SummaryBox.
   * Dependant on product
   */
  get colorScheme(): SummaryBoxColorScheme {
    return this.args.product === 'maths' ? SummaryBoxColorScheme.DarkGreen : SummaryBoxColorScheme.LightBlue
  }

  /**
   * List of summary statistics, extracted from the input data.
   * Display total, gold, silver and bronze counts.
   */
  get summaryStats(): Stat[] {
    const { intl } = this
    const { gold, silver, bronze } = this.args.data.summary

    const GoldLabel = intl.t('reporting.charts.awardsEarned.certificates.goldLabel')
    const SilverLabel = intl.t('reporting.charts.awardsEarned.certificates.silverLabel')
    const BronzeLabel = intl.t('reporting.charts.awardsEarned.certificates.bronzeLabel')
    const TotalLabel = intl.t('reporting.charts.awardsEarned.certificates.totalLabel')

    const total = gold + silver + bronze

    return [
      { label: GoldLabel, value: formatNumber(gold, 0) },
      { label: SilverLabel, value: formatNumber(silver, 0) },
      { label: BronzeLabel, value: formatNumber(bronze, 0) },
      { label: TotalLabel, value: formatNumber(total, 0) },
    ]
  }

  <template>
    <div data-test-awards-earned-certificates-totals class="h-full" ...attributes>
      <LeftSplitChart>
        <:left>
          <SummaryBox
            class="h-full"
            @title={{@summaryTitle}}
            @grade={{@grade}}
            @stats={{this.summaryStats}}
            @colorScheme={{this.colorScheme}}
          />
        </:left>
        <:right>
          <SeriesColumn
            @data={{this.chartData}}
            @categories={{this.categories}}
            @stackingStyle="normal"
            @legend={{this.legend}}
            @yAxisLabel={{@yAxisTitle}}
            @title={{@title}}
          />
        </:right>
      </LeftSplitChart>
    </div>
  </template>
}

export default AwardsEarnedCertificatesTotals
